import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import SEOHead from './SEOHead';

const {
  FiArrowLeft, FiArrowRight, FiEdit3, FiZap, FiCheckCircle, FiAlertTriangle,
  FiRotateCw, FiX, FiBookOpen, FiTarget, FiPlus, FiLock, FiFeather
} = FiIcons;

const CHAPTERS = [
  {
    id: 'c1', title: '1 · Arrivals',
    text: `The flight from Kyiv landed at JFK forty minutes early, which Elena chose to take as a sign. She had not been back to New York in eleven years — not since the winter she'd left for the fellowship in Vienna and let everything else follow her out the door. The terminal smelled of burnt coffee and floor wax, exactly as she remembered. Some cities have the grace to change; New York simply adds a new coat over the old one and calls it renovation.`
  },
  {
    id: 'c2', title: '2 · The Manuscript',
    text: `The apartment on West 83rd Street had belonged to her mentor, Professor Haas, who had died in February leaving three boxes of unpublished letters and a cat named Brodsky. The cat sat on the manuscript she was trying to read — forty typed pages, yellow at the edges, a carbon copy of something that had no business being a carbon copy in 1998. Across the top, in her mentor's precise hand: "For whoever finds this. Do not publish before the Kyiv archive opens."`
  },
  {
    id: 'c3', title: '3 · Rain on Rue Lepic',
    text: `Paris in November is an argument in favor of leaving. Mireille stood under the awning of the bouquiniste across from Shakespeare and Company, watching the Seine go the color of pewter. She had come to return a book — a first edition of Simenon she'd borrowed without asking — and had spent forty minutes not going in. The river was very still. Somewhere behind her a tourist photographed everything at once and photographed nothing.`
  }
];

// Canned, in-voice AI outputs (rotate for "try again")
const AI = {
  continue: [
    `She took the longer route through the park, past the bench where her grandfather used to read the paper, past the fountain that had been dry since the renovation and was dry still. Some absences become landmarks.`,
    `The letters were dated, but not in order. She spread them across the kitchen table and read them in the order they fell, which turned out to be the right order after all.`,
    `JFK at arrivals is where everyone looks like they are waiting for the wrong person. Elena watched the doors open and close and felt, briefly, that she was one of those people too.`
  ],
  describe: [
    `The manuscript smelled of cigarette smoke and library — that particular stillness that paper develops when no one has touched it in decades. She turned a page and a small card fell out, a café receipt from a place called Molnár on Váci utca, Budapest, 1994.`,
    `West 83rd at night: the brownstones holding their light inside like a secret, the ginkgo trees still dropping their yellow after every rain. The neighborhood had gentrified twice since she'd left and was exactly the same.`,
    `The Seine at dusk goes from silver to bronze before it settles into black, and at no point in this sequence does it ask for your attention. It simply takes it.`
  ],
  rewrite: [
    `Elena hadn't been to New York since the year she let herself become European — or tried to. The terminal was unchanged: burnt coffee, announcement echo, the particular despair of the arrivals hall.`,
    `The manuscript was forty pages of what looked, at first, like grief. On the second read it looked like evidence.`,
    `Mireille was good at not going in. She had practiced it her whole adult life — standing outside the thing she wanted, studying it through glass.`
  ],
  tighten: [
    `Eleven years since JFK. The terminal still smelled the same. Some cities don't change; they layer.`,
    `Forty pages. A cat on top. A note she shouldn't have read but had already read. She poured more coffee.`,
    `The book could wait. The river could not. She crossed the bridge and didn't look back, which was the kind of decision she was good at.`
  ]
};

const ACTIONS = [
  { key: 'continue', label: 'Continue', icon: FiArrowRight, mode: 'append', hint: 'Write the next lines in your voice' },
  { key: 'describe', label: 'Describe more', icon: FiZap, mode: 'append', hint: 'Add sensory detail in your style' },
  { key: 'rewrite', label: 'Rewrite opening', icon: FiEdit3, mode: 'replaceFirst', hint: 'Re-voice the first sentence' },
  { key: 'tighten', label: 'Tighten', icon: FiTarget, mode: 'replaceAll', hint: 'Same scene, fewer words' }
];

const wordCount = (t) => (t.trim() ? t.trim().split(/\s+/).length : 0);

const EditorDemoPage = () => {
  const [activeId, setActiveId] = useState('c3');
  const initial = CHAPTERS.find((c) => c.id === activeId).text;
  const [text, setText] = useState(initial);
  const [generating, setGenerating] = useState(false);
  const [suggestion, setSuggestion] = useState(null); // {action, content, mode}
  const [rot, setRot] = useState(0);
  const [voiceMatch, setVoiceMatch] = useState(88);
  const [accepted, setAccepted] = useState(0);

  // lead capture
  const [lead, setLead] = useState({ name: '', email: '' });
  const [leadDone, setLeadDone] = useState(false);

  const words = useMemo(() => wordCount(text), [text]);
  const goal = 1500;

  const loadChapter = (id) => {
    const c = CHAPTERS.find((x) => x.id === id);
    setActiveId(id); setText(c.text); setSuggestion(null); setVoiceMatch(88); setAccepted(0);
  };

  const runAction = (action) => {
    setGenerating(true); setSuggestion(null);
    const pool = AI[action.key];
    const content = pool[rot % pool.length];
    window.setTimeout(() => {
      setGenerating(false);
      setSuggestion({ action, content, mode: action.mode });
    }, 650);
  };

  const tryAgain = () => { setRot((r) => r + 1); if (suggestion) runAction(suggestion.action); };

  const insert = () => {
    if (!suggestion) return;
    setText((prev) => {
      if (suggestion.mode === 'append') return `${prev.trim()} ${suggestion.content}`;
      if (suggestion.mode === 'replaceFirst') {
        const rest = prev.replace(/^[^.!?]*[.!?]\s*/, '');
        return `${suggestion.content} ${rest}`.trim();
      }
      return suggestion.content; // replaceAll
    });
    setSuggestion(null);
    setRot((r) => r + 1);
    setVoiceMatch(94);
    setAccepted((a) => a + 1);
  };

  const submitLead = (e) => {
    e.preventDefault();
    if (!lead.name.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(lead.email)) return;
    // eslint-disable-next-line no-console
    console.log('Demo early-access lead:', lead);
    setLeadDone(true);
  };

  const preflight = [
    ['Chapter heading styled', true],
    ['Smart quotes & em-dashes', true],
    ['No double spaces', !text.includes('  ')],
    ['Scene length on track', words > 40]
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-parchment-50 to-parchment-100">
      <SEOHead
        title="Try the Editor - Live Demo | ManuscriptSmith"
        description="Play with the ManuscriptSmith writing editor: write a scene, tap AI actions, and watch suggestions appear in your voice with a live Voice-match meter and KDP Preflight. No signup to try."
        keywords="ai writing editor demo, try ai book writing, voice dna demo, interactive writing studio"
        canonicalUrl="https://manuscriptsmith.com/editor"
      />

      <main>
        <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 pb-6 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-3">
            Try the editor — <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary-500 to-secondary-500">in your voice</span>
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            This is a guided taste of the real studio. Edit the scene, then tap an AI action and watch a suggestion
            appear in the author's voice. Accept it and the Voice-match meter climbs.
          </p>
        </section>

        {/* Editor */}
        <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pb-10">
          <div className="bg-gray-900 rounded-2xl shadow-2xl overflow-hidden">
            {/* chrome */}
            <div className="flex items-center gap-2 px-4 py-2.5 border-b border-gray-700">
              <span className="w-3 h-3 rounded-full bg-red-500" /><span className="w-3 h-3 rounded-full bg-yellow-500" /><span className="w-3 h-3 rounded-full bg-green-500" />
              <span className="mx-auto text-xs text-gray-400">ManuscriptSmith Studio — The Lighthouse Year</span>
            </div>

            <div className="grid lg:grid-cols-[180px_1fr_240px]">
              {/* sidebar */}
              <div className="bg-gray-850 bg-gray-800/60 border-r border-gray-700 p-3">
                <div className="text-[10px] font-bold uppercase text-gray-500 mb-2">Chapters</div>
                {CHAPTERS.map((c) => (
                  <button key={c.id} onClick={() => loadChapter(c.id)}
                    className={`w-full text-left text-[12px] px-2 py-1.5 rounded mb-1 transition-colors ${activeId === c.id ? 'bg-primary-600/30 text-primary-200 font-semibold' : 'text-gray-400 hover:bg-gray-700/50'}`}>
                    {c.title}
                  </button>
                ))}
                <button className="w-full text-left text-[12px] px-2 py-1.5 rounded text-gray-500 inline-flex items-center gap-1 hover:bg-gray-700/50">
                  <SafeIcon icon={FiPlus} className="w-3 h-3" /> New chapter
                </button>
              </div>

              {/* editor body */}
              <div className="bg-white p-5 sm:p-8">
                <div className="max-w-xl mx-auto">
                  <div className="text-xs text-gray-400 mb-3">Chapter {CHAPTERS.find(c=>c.id===activeId).title}</div>
                  <textarea
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    rows={8}
                    spellCheck={true}
                    lang="en"
                    className="w-full resize-none outline-none text-gray-800 leading-8 text-[17px] bg-transparent"
                    style={{ fontFamily: 'Fraunces, Georgia, serif' }}
                  />

                  {/* AI action bar */}
                  <div className="mt-4 flex flex-wrap gap-2">
                    {ACTIONS.map((a) => (
                      <button key={a.key} onClick={() => runAction(a)} title={a.hint} disabled={generating}
                        className="inline-flex items-center gap-1.5 text-[13px] font-medium px-3 py-2 rounded-lg bg-gray-900 text-white hover:bg-gray-800 disabled:opacity-50 transition-colors">
                        <SafeIcon icon={a.icon} className="w-3.5 h-3.5 text-secondary-400" /> {a.label}
                      </button>
                    ))}
                    <button onClick={() => loadChapter(activeId)} title="Restore the original scene"
                      className="inline-flex items-center gap-1.5 text-[13px] font-medium px-3 py-2 rounded-lg border border-gray-300 text-gray-500 hover:bg-gray-50 transition-colors">
                      <SafeIcon icon={FiRotateCw} className="w-3.5 h-3.5" /> Reset scene
                    </button>
                  </div>

                  {/* generating / suggestion */}
                  <AnimatePresence>
                    {generating && (
                      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                        className="mt-4 text-sm text-primary-600 inline-flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full bg-primary-500 animate-pulse" /> Generating in your voice…
                      </motion.div>
                    )}
                    {suggestion && !generating && (
                      <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                        className="mt-4 border-2 border-primary-200 bg-primary-50/60 rounded-xl p-4">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-[11px] font-bold uppercase tracking-wide text-primary-600 inline-flex items-center gap-1">
                            <SafeIcon icon={FiFeather} className="w-3.5 h-3.5" /> {suggestion.action.label} · in your voice
                          </span>
                          <span className="text-[11px] text-gray-400">94% voice match</span>
                        </div>
                        <p className="text-gray-800 italic leading-7" style={{ fontFamily: 'Fraunces, Georgia, serif' }}>{suggestion.content}</p>
                        <div className="mt-3 flex gap-2">
                          <button onClick={insert} className="inline-flex items-center gap-1 text-[13px] font-bold px-3 py-1.5 rounded-lg bg-primary-600 text-white hover:bg-primary-700">
                            <SafeIcon icon={FiCheckCircle} className="w-4 h-4" /> Insert
                          </button>
                          <button onClick={tryAgain} className="inline-flex items-center gap-1 text-[13px] font-medium px-3 py-1.5 rounded-lg border border-gray-300 text-gray-600 hover:bg-white">
                            <SafeIcon icon={FiRotateCw} className="w-4 h-4" /> Try again
                          </button>
                          <button onClick={() => setSuggestion(null)} className="inline-flex items-center gap-1 text-[13px] font-medium px-3 py-1.5 rounded-lg text-gray-400 hover:text-gray-600">
                            <SafeIcon icon={FiX} className="w-4 h-4" /> Dismiss
                          </button>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </div>

              {/* right rail */}
              <div className="bg-gray-800/60 border-l border-gray-700 p-4 text-gray-200">
                {/* voice match */}
                <div className="mb-5">
                  <div className="flex justify-between text-[11px] text-gray-400 mb-1"><span>Voice match</span><span>{voiceMatch}%</span></div>
                  <div className="w-full bg-gray-700 rounded-full h-2.5">
                    <motion.div className="h-2.5 rounded-full bg-gradient-to-r from-primary-500 to-secondary-400"
                      animate={{ width: `${voiceMatch}%` }} transition={{ duration: 0.6 }} />
                  </div>
                  <div className="mt-2 flex flex-wrap gap-1">
                    {['rhythm', 'em-dash', 'imagery'].map((t) => (
                      <span key={t} className="text-[10px] px-2 py-0.5 rounded-full bg-primary-600/25 text-primary-200">{t}</span>
                    ))}
                  </div>
                </div>
                {/* preflight */}
                <div className="mb-5">
                  <div className="text-[11px] font-bold uppercase text-gray-400 mb-2">Print Preflight</div>
                  <div className="space-y-1.5">
                    {preflight.map(([label, ok]) => (
                      <div key={label} className="flex items-center gap-2 text-[12px]">
                        <SafeIcon icon={ok ? FiCheckCircle : FiAlertTriangle} className={`w-4 h-4 ${ok ? 'text-green-400' : 'text-amber-400'}`} />
                        <span className="text-gray-300">{label}</span>
                      </div>
                    ))}
                  </div>
                </div>
                {/* progress */}
                <div>
                  <div className="flex justify-between text-[11px] text-gray-400 mb-1"><span>Today</span><span>{words} / {goal} words</span></div>
                  <div className="w-full bg-gray-700 rounded-full h-2"><div className="h-2 rounded-full bg-secondary-500" style={{ width: `${Math.min(100, (words / goal) * 100)}%` }} /></div>
                  <div className="mt-2 text-[11px] text-gray-500">{accepted} AI suggestion{accepted === 1 ? '' : 's'} accepted</div>
                </div>
              </div>
            </div>
          </div>
          <p className="text-center text-xs text-gray-400 mt-3">Guided demo with sample text. The real studio runs Author Voice DNA on <em>your</em> writing.</p>
        </section>

        {/* Lead capture → full interface */}
        <section className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pb-24">
          <div className="bg-gradient-to-r from-primary-500 to-secondary-500 rounded-2xl p-8 text-white shadow-xl overflow-hidden">
            {!leadDone ? (
              <>
                <div className="text-center mb-6">
                  <h2 className="text-2xl md:text-3xl font-bold mb-2">Want the full interface?</h2>
                  <p className="opacity-90">Drop your name and email and we'll send you early access to the complete writing studio — and your own Voice Memory.</p>
                </div>
                <form onSubmit={submitLead} className="flex flex-col sm:flex-row gap-3 w-full max-w-xl mx-auto">
                  <input value={lead.name} onChange={(e) => setLead({ ...lead, name: e.target.value })} placeholder="Your name"
                    className="flex-1 min-w-0 px-4 py-3 rounded-lg text-gray-800 outline-none" />
                  <input value={lead.email} onChange={(e) => setLead({ ...lead, email: e.target.value })} type="email" placeholder="you@email.com"
                    className="flex-1 min-w-0 px-4 py-3 rounded-lg text-gray-800 outline-none" />
                  <button type="submit" className="w-full sm:w-auto px-6 py-3 bg-white text-primary-600 font-bold rounded-lg hover:bg-gray-50 sm:whitespace-nowrap">
                    Get early access
                  </button>
                </form>
                <p className="text-center text-xs opacity-80 mt-3 inline-flex items-center gap-1 justify-center w-full">
                  <SafeIcon icon={FiLock} className="w-3 h-3" /> No spam. Your writing stays yours.
                </p>
              </>
            ) : (
              <div className="text-center py-4">
                <SafeIcon icon={FiCheckCircle} className="w-12 h-12 mx-auto mb-3" />
                <h2 className="text-2xl font-bold mb-2">You're on the list, {lead.name.split(' ')[0]}!</h2>
                <p className="opacity-90">We'll email you early access to the full studio. In the meantime, keep playing with the demo above.</p>
              </div>
            )}
          </div>
        </section>
      </main>
    </div>
  );
};

export default EditorDemoPage;
